import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mountain, ArrowRight, Check, MapPin, Home, Shield, Star, BarChart3, Users, Globe } from 'lucide-react';
import StateSelector from '../components/StateSelector';
import HimalayanPeaksFooter from '../components/HimalayanPeaksFooter';

const DISTRICTS = [
  { id: 'aizawl', name: 'Aizawl', homestays: 280, valleys: ['Durtlang Hills', 'Solomon\'s Temple', 'Reiek', 'Falkawn'], altitude: '900–1,400m', highlight: true, division: 'Central' },
  { id: 'champhai', name: 'Champhai', homestays: 180, valleys: ['Rih Dil Lake', 'Mura Puk', 'Thasiama Seno', 'Vaphai'], altitude: '800–2,000m', highlight: true, division: 'Eastern' },
  { id: 'lunglei', name: 'Lunglei', homestays: 160, valleys: ['Lunglei Bridge', 'Saikuti Hall', 'Thorang', 'Zobawk'], altitude: '700–1,500m', highlight: true, division: 'Southern' },
  { id: 'serchhip', name: 'Serchhip', homestays: 100, valleys: ['Vantawng Falls', 'Mat River', 'Chhingchhip', 'Thenzawl'], altitude: '500–1,600m', highlight: true, division: 'Central' },
  { id: 'kolasib', name: 'Kolasib', homestays: 90, valleys: ['Rengtekawn', 'Tlawng River', 'Bairabi', 'Kawnpui'], altitude: '200–1,000m', highlight: true, division: 'Northern' },
  { id: 'lawngtlai', name: 'Lawngtlai', homestays: 70, valleys: ['Ngengpui WLS', 'Palak Lake', 'Sangau', 'Chawngte'], altitude: '200–1,200m', highlight: false, division: 'Southern' },
  { id: 'mamit', name: 'Mamit', homestays: 60, valleys: ['Dampa Tiger Reserve', 'West Phaileng', 'Reiek Tlang', 'Zawlnuam'], altitude: '200–1,400m', highlight: false, division: 'Western' },
  { id: 'siaha', name: 'Siaha', homestays: 50, valleys: ['Phawngpui Peak', 'Blue Mountain NP', 'Tuipui', 'Lawngtlai'], altitude: '800–2,157m', highlight: true, division: 'Southern' },
];

const TOTAL_HOMESTAYS = DISTRICTS.reduce((sum, d) => sum + d.homestays, 0);

const TIERS = [
  {
    name: 'Registered',
    tagline: 'State-Licensed Homestays',
    desc: 'Every homestay registered with the Mizoram Tourism Department, from Aizawl\'s ridgetop capital to the Blue Mountain\'s cloud forests, digitized on one platform.',
    icon: Home,
    color: '#8B9DAF',
    features: ['License verification badge', 'Location on hill map', 'Basic contact listing', 'Searchable by district & valley'],
  },
  {
    name: 'Silver',
    tagline: 'GoHimalayan Quality Assured',
    desc: 'Registered homestays editorially visited and quality-verified. Verified ridge stays, bamboo retreats, and traveller-ready listings.',
    icon: Shield,
    color: '#A8B8C8',
    features: ['On-ground quality verification', 'Professional photography', 'AI-curated descriptions', 'Review management', 'Priority in search results'],
  },
  {
    name: 'Gold',
    tagline: 'The GoHimalayan Collective',
    desc: 'Premium partners with full storytelling pages, active marketing, direct booking, and dedicated account management. The hidden gems of the Blue Mountains.',
    icon: Star,
    color: '#D4A853',
    features: ['Luxury storytelling page', 'Direct booking integration', 'Social media marketing', 'Revenue analytics dashboard', 'Dedicated account manager', 'Featured in campaigns'],
  },
];

const VALUE_PROPS = [
  { icon: Globe, title: 'Digital Registry', desc: 'Every licensed homestay from Aizawl\'s ridgetop to Phawngpui\'s Blue Mountain — searchable, verified, bookable on a single platform.' },
  { icon: BarChart3, title: 'Demand Intelligence', desc: 'Real-time traveller search data, Chapchar Kut festival trends, and bamboo-season occupancy insights for Mizoram tourism planning.' },
  { icon: Users, title: 'Host Enablement', desc: 'Training, photography, and digital literacy — empowering Mizo, Lai, and Mara communities to share their egalitarian highland culture.' },
  { icon: Shield, title: 'Quality Assurance', desc: 'On-ground verification ensuring each homestay honours the Mizo tradition of Tlawmngaihna — selfless hospitality above all else.' },
];

function MizoramMap({ activeDistrict, setActiveDistrict }) {
  const districtPaths = {
    'kolasib': { d: 'M120 20 L220 15 L240 60 L200 90 L140 85 L110 55 Z', cx: 170, cy: 52 },
    'mamit': { d: 'M30 50 L110 55 L140 85 L120 130 L60 140 L25 100 Z', cx: 80, cy: 95 },
    'aizawl': { d: 'M140 85 L200 90 L230 130 L210 175 L150 180 L120 140 L120 130 Z', cx: 175, cy: 135 },
    'champhai': { d: 'M230 130 L310 110 L340 155 L320 200 L260 210 L210 175 Z', cx: 275, cy: 165 },
    'serchhip': { d: 'M150 180 L210 175 L260 210 L250 260 L190 270 L140 240 Z', cx: 200, cy: 225 },
    'lunglei': { d: 'M60 140 L120 140 L140 240 L190 270 L170 320 L100 330 L50 290 L40 220 Z', cx: 110, cy: 250 },
    'lawngtlai': { d: 'M100 330 L170 320 L210 360 L180 400 L110 395 L70 365 Z', cx: 140, cy: 363 },
    'siaha': { d: 'M190 270 L250 260 L290 290 L280 340 L230 360 L210 360 L170 320 Z', cx: 235, cy: 315 },
  };

  return (
    <svg viewBox="0 0 370 420" className="w-full max-w-md mx-auto" style={{ filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))' }}>
      {Object.entries(districtPaths).map(([id, path]) => {
        const district = DISTRICTS.find(d => d.id === id);
        const isActive = activeDistrict === id;
        const isHighlight = district?.highlight;
        return (
          <g key={id} onMouseEnter={() => setActiveDistrict(id)} onMouseLeave={() => setActiveDistrict(null)} style={{ cursor: 'pointer' }}>
            <path d={path.d}
              fill={isActive ? '#6B8FBF' : isHighlight ? 'rgba(107,143,191,0.35)' : 'rgba(255,255,255,0.12)'}
              stroke={isActive ? '#9CBDE0' : 'rgba(255,255,255,0.3)'} strokeWidth={isActive ? 2 : 1}
              style={{ transition: 'all 0.3s ease' }} />
            <text x={path.cx} y={path.cy} textAnchor="middle" dominantBaseline="middle"
              fill={isActive ? '#fff' : 'rgba(255,255,255,0.65)'} fontSize={isActive ? 10 : 8}
              fontFamily="Georgia, serif" style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}>
              {district?.name}
            </text>
            {isActive && (
              <text x={path.cx} y={path.cy + 14} textAnchor="middle" dominantBaseline="middle"
                fill="#9CBDE0" fontSize={8} fontFamily="Georgia, serif" style={{ pointerEvents: 'none' }}>
                {district?.homestays} homestays
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function AnimatedNumber({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else { setCount(Math.floor(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span>{count.toLocaleString()}+</span>;
}

export default function MizoramStatePage() {
  const [activeDistrict, setActiveDistrict] = useState(null);
  const activeData = DISTRICTS.find(d => d.id === activeDistrict);

  return (
    <div className="min-h-screen bg-[#141c24]" data-testid="mizoram-state-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#141c24]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <Mountain className="w-5 h-5" />
            <span className="text-sm tracking-[0.15em] uppercase">GoHimalayan</span>
          </Link>
          <StateSelector current="mizoram" />
        </div>
      </nav>

      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1660644959898-1b97e0ba141a?w=1920&q=80"
            alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#141c24]/40 via-[#141c24]/40 to-[#141c24]/95" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-sm px-5 py-2 rounded-full mb-10">
            <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            <span className="text-white/70 text-[10px] tracking-[0.4em] uppercase">Mizoram &middot; Land of the Hill People</span>
          </div>
          <h1 className="text-7xl sm:text-8xl md:text-9xl font-light text-white tracking-tight mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 4px 30px rgba(0,0,0,0.4)' }}>
            <AnimatedNumber target={TOTAL_HOMESTAYS} />
          </h1>
          <p className="text-white/70 text-sm tracking-[0.3em] uppercase mb-12">
            Licensed Homestays &middot; 11 Districts &middot; One Platform
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed max-w-3xl mx-auto mb-14"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Where blue mountains fold into infinity,<br />and hospitality is the highest virtue
          </h2>
        </div>
      </section>

      <section className="pt-24 pb-8 px-6 relative" data-testid="story-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#6B8FBF] text-[10px] tracking-[0.5em] uppercase mb-6">The Blue Mountains</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed mb-8"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            In Mizoram, a stranger at the door<br />
            is never a stranger for long.
          </h2>
          <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto">
            Aizawl, the capital, is built entirely on ridges — a city that climbs rather than spreads, 
            where churches perch on hilltops and community singing echoes through valleys on Sunday 
            mornings. The Mizo concept of Tlawmngaihna — putting others before self, helping without 
            being asked — defines every interaction. At Phawngpui, the Blue Mountain and highest peak 
            of Mizoram, rhododendrons bloom crimson above cloud banks that make the hills appear to 
            float. At Vantawng Falls, the tallest in the state, water plunges into bamboo forests 
            so thick they create their own weather. In Champhai, the rice bowl of Mizoram, terraced 
            paddies descend to the Myanmar border. The Mizo people have no caste system, no feudal 
            history — only a deep, egalitarian tradition of communal living where every home is open 
            and every meal is shared.
          </p>
        </div>
      </section>

      <section className="py-24 px-6 relative" data-testid="living-map-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#6B8FBF] text-[10px] tracking-[0.5em] uppercase mb-4">The Living Map</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Ridge capital to blue mountain. One highland.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative"><MizoramMap activeDistrict={activeDistrict} setActiveDistrict={setActiveDistrict} /></div>
            <div className="space-y-6">
              {activeData ? (
                <div className="transition-all duration-300">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-[#6B8FBF] text-[10px] tracking-[0.4em] uppercase">Region</p>
                    <span className="text-white/20 text-[10px]">&middot;</span>
                    <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase">{activeData.division} Mizoram</p>
                  </div>
                  <h3 className="text-4xl text-white font-light italic mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.name}</h3>
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="text-5xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.homestays}</span>
                    <span className="text-white/40 text-sm tracking-wider uppercase">Registered Homestays</span>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-white/40" /><span className="text-white/60 text-sm">{activeData.altitude}</span></div>
                    <div className="flex flex-wrap gap-2">{activeData.valleys.map(v => (<span key={v} className="text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full border border-white/15 text-white/55">{v}</span>))}</div>
                  </div>
                </div>
              ) : (
                <div className="text-center lg:text-left">
                  <p className="text-white/20 text-sm italic mb-6">Hover over a region to explore</p>
                  <div className="grid grid-cols-2 gap-4">
                    {DISTRICTS.filter(d => d.highlight).map(d => (
                      <div key={d.id} className="border border-white/10 rounded-lg p-4 hover:border-white/25 transition-colors cursor-pointer" onMouseEnter={() => setActiveDistrict(d.id)}>
                        <p className="text-white/80 text-sm mb-1">{d.name}</p>
                        <p className="text-2xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{d.homestays}</p>
                        <p className="text-white/30 text-[10px] tracking-wider uppercase mt-1">homestays</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-white/10 pt-6 mt-8">
                <div className="flex items-center justify-between">
                  <span className="text-white/45 text-xs tracking-wider uppercase">Total across Mizoram</span>
                  <span className="text-white text-lg font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{TOTAL_HOMESTAYS.toLocaleString()} homestays</span>
                </div>
                <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#6B8FBF] to-[#9CBDE0] rounded-full" style={{ width: '100%', animation: 'expandBar 2s ease-out' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 border-t border-white/10" data-testid="tier-funnel">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#6B8FBF] text-[10px] tracking-[0.5em] uppercase mb-4">The Homestay Index</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Three tiers. One ecosystem.</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto leading-relaxed">Every homestay begins as Registered. Through quality verification and partnership, they rise — from Silver to Gold.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((tier, i) => (
              <div key={tier.name} className="relative border border-white/10 rounded-xl p-8 hover:border-white/25 transition-all group" style={{ background: `linear-gradient(160deg, ${tier.color}12, transparent)` }}>
                <div className="absolute top-6 right-6 text-white/10 text-6xl font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{i + 1}</div>
                <tier.icon className="w-8 h-8 mb-6" style={{ color: tier.color }} />
                <h3 className="text-xl text-white font-light mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{tier.name}</h3>
                <p className="text-xs tracking-wider uppercase mb-4" style={{ color: tier.color }}>{tier.tagline}</p>
                <p className="text-white/45 text-sm leading-relaxed mb-6">{tier.desc}</p>
                <ul className="space-y-2.5">{tier.features.map(f => (<li key={f} className="flex items-start gap-2"><Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tier.color }} /><span className="text-white/55 text-sm">{f}</span></li>))}</ul>
                {i < 2 && <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10"><ArrowRight className="w-5 h-5 text-white/10" /></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 relative overflow-hidden" data-testid="b2g-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#141c24] via-[#182330] to-[#141c24]" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-[#6B8FBF] text-[10px] tracking-[0.5em] uppercase mb-4">For Tourism Departments</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-white font-light italic tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              We digitize your homestay registry.<br /><span className="text-white/55">You focus on policy.</span>
            </h2>
            <p className="text-white/45 text-sm max-w-2xl mx-auto leading-relaxed">GoHimalayan serves as the technology and marketing partner — bringing every licensed homestay across Mizoram's blue hills online and discoverable.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-20">
            {VALUE_PROPS.map(prop => (
              <div key={prop.title} className="border border-white/10 rounded-xl p-8 hover:border-white/20 transition-colors">
                <prop.icon className="w-6 h-6 text-[#6B8FBF] mb-5" />
                <h3 className="text-lg text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{prop.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed">{prop.desc}</p>
              </div>
            ))}
          </div>
          <div className="border border-[#6B8FBF]/20 rounded-xl p-10 sm:p-14 text-center" style={{ background: 'linear-gradient(160deg, rgba(107,143,191,0.05), transparent)' }}>
            <p className="text-[#6B8FBF] text-[10px] tracking-[0.5em] uppercase mb-6">The Partnership Model</p>
            <h3 className="text-2xl sm:text-3xl text-white font-light italic mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Built to scale. Designed to empower.</h3>
            <div className="max-w-2xl mx-auto space-y-4 mb-10">
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#6B8FBF]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#6B8FBF] text-xs">1</span></div>
                <p className="text-white/60 text-sm">Mizoram shares its licensed homestay registry — GoHimalayan digitizes and maps every property across 11 districts</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#6B8FBF]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#6B8FBF] text-xs">2</span></div>
                <p className="text-white/60 text-sm">Travellers discover, compare, and book directly — driving revenue into ridge communities, bamboo-forest villages, and highland homestays</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#6B8FBF]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#6B8FBF] text-xs">3</span></div>
                <p className="text-white/60 text-sm">State receives real-time demand intelligence, Chapchar Kut analytics, and eco-tourism planning dashboards</p>
              </div>
            </div>
            <a href="https://wa.me/919871155963?text=Hi%20GoHimalayan%2C%20I%27d%20like%20to%20discuss%20the%20State%20Partnership%20Program%20for%20Mizoram."
              className="inline-flex items-center gap-3 bg-[#6B8FBF] text-[#0a0f14] px-8 py-3.5 rounded-full text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#8badd0] transition-colors"
              data-testid="b2g-cta" target="_blank" rel="noopener noreferrer">
              Begin Conversation <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <HimalayanPeaksFooter accentColor="#6B8FBF" />
      <style>{`@keyframes expandBar { from { width: 0; } to { width: 100%; } }`}</style>
    </div>
  );
}
